import { useDrag, useDragLayer, useDrop } from 'react-dnd';
import { useEffect, useRef, useState } from 'react';

const ItemType = 'ITEM_CARD';

let nextRowKey = 0;
/** Stable React key for a challenge that has no database id yet. */
export const newRowKey = () => `new-${(nextRowKey += 1)}`;

export default function DraggableItemCard({ index, item, moveItem, children, total = 2 }) {
  const cardRef = useRef(null);
  const handleRef = useRef(null);

  const [, drop] = useDrop({
    accept: ItemType,
    hover(draggedItem, monitor) {
      if (!cardRef.current) return;
      if (draggedItem.index === index) return;

      const hoverBoundingRect = cardRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (draggedItem.index < index && hoverClientY < hoverMiddleY) return;
      if (draggedItem.index > index && hoverClientY > hoverMiddleY) return;

      moveItem(draggedItem.index, index);
      draggedItem.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Each card is taller than the screen (rich text editor), so another card could never
  // be reached while dragging. Fold them all down during a drag. Delayed because Chrome
  // cancels a drag if the page changes inside dragstart. Hidden, not unmounted, so typed
  // text survives.
  const anyDragging = useDragLayer((monitor) => monitor.isDragging());
  const [folded, setFolded] = useState(false);
  useEffect(() => {
    if (!anyDragging) {
      setFolded(false);
      return undefined;
    }
    const timer = setTimeout(() => setFolded(true), 50);
    return () => clearTimeout(timer);
  }, [anyDragging]);

  drag(handleRef);
  drop(cardRef);

  const title = item?.itemname || item?.item_name || '';

  return (
    <div
      ref={cardRef}
      style={{
        opacity: isDragging ? 0.5 : 1,
        marginBottom: '12px',
      }}
    >
      {total > 1 ? (
        <div
          ref={handleRef}
          aria-label="Drag to reorder challenge"
          title="Drag to reorder"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '6px',
            padding: '4px 8px',
            borderRadius: '6px',
            border: '1px solid rgba(15, 23, 42, 0.12)',
            backgroundColor: '#f8fafc',
            color: '#64748b',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          <span aria-hidden="true" style={{ letterSpacing: '2px', lineHeight: 1 }}>
            ⋮⋮
          </span>
          Drag to reorder
        </div>
      ) : null}
      {folded ? (
        <div className="card p-3 mb-0 fw-semibold">
          Challenge {index + 1}{title ? `: ${title}` : ''}
        </div>
      ) : null}
      <div style={{ display: folded ? 'none' : 'block' }}>{children}</div>
    </div>
  );
}
